/**
 * Tâche 1443 (Eric 2026-10-02) — Un terme cible et ses variantes erronées.
 *
 * Les propositions de l'IA sont montrées À PART, en haut, avec deux gestes
 * explicites (valider / refuser) : appliquées d'office, une « faute » inventée
 * corrigerait un mot correct (acquis 1422 sur les faux positifs).
 */
import { useState } from "react";
import {
  Check, PencilSimple, Plus, Sparkle, SpinnerGap, Trash, X,
} from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";

export const TermeCard = ({ terme, onAjouter, onRetirer, onStatut, onSuggerer,
                            onRenommer, onSupprimer, occurrences = null }) => {
  const { tr } = useLang();
  const [saisie, setSaisie] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [edition, setEdition] = useState(false);
  const [nouveauNom, setNouveauNom] = useState(terme.terme);

  const enAttente = terme.variantes.filter((v) => v.statut === "en_attente");
  const actives = terme.variantes.filter((v) => v.statut === "active");
  const refusees = terme.variantes.filter((v) => v.statut === "refusee");

  const ajouter = async () => {
    const textes = saisie.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    if (!textes.length) return;
    setEnCours(true);
    await onAjouter(terme.id, textes);
    setSaisie("");
    setEnCours(false);
  };

  const suggerer = async () => {
    setEnCours(true);
    await onSuggerer(terme.id);
    setEnCours(false);
  };

  return (
    <div className="border border-zinc-200 bg-white rounded-xl p-4"
         data-testid={`orthographe-terme-${terme.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          {edition ? (
            <div className="flex items-center gap-2">
              <input
                value={nouveauNom}
                onChange={(e) => setNouveauNom(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1 border border-zinc-300 rounded-md text-sm"
                data-testid={`orthographe-renommer-champ-${terme.id}`}
              />
              <button
                type="button"
                onClick={async () => { await onRenommer(terme.id, nouveauNom); setEdition(false); }}
                className="p-1.5 rounded-md bg-[#002FA7] text-white"
                data-testid={`orthographe-renommer-ok-${terme.id}`}
                title={tr("Enregistrer", "Save")}
              >
                <Check size={14} />
              </button>
              <button
                type="button"
                onClick={() => { setNouveauNom(terme.terme); setEdition(false); }}
                className="p-1.5 rounded-md bg-zinc-100 text-zinc-600"
                data-testid={`orthographe-renommer-annuler-${terme.id}`}
                title={tr("Annuler", "Cancel")}
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-zinc-900 break-words"
                    data-testid={`orthographe-terme-nom-${terme.id}`}>
                {terme.terme}
              </span>
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">
                {actives.length} {tr("variante(s) active(s)", actives.length === 1 ? "active variant" : "active variants")}
              </span>
              {enAttente.length > 0 && (
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap"
                      data-testid={`orthographe-attente-badge-${terme.id}`}>
                  {enAttente.length} {tr("à valider", "to review")}
                </span>
              )}
              {occurrences != null && (
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 whitespace-nowrap">
                  {occurrences} {tr("occurrence(s) en base", occurrences === 1 ? "occurrence in the database" : "occurrences in the database")}
                </span>
              )}
            </div>
          )}
          {terme.notes && (
            <p className="text-[11px] text-zinc-500 mt-1">{terme.notes}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={suggerer}
            disabled={enCours}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-violet-200 bg-violet-50 text-violet-700 text-[11px] font-medium hover:bg-violet-100 transition-colors disabled:opacity-50"
            data-testid={`orthographe-suggerer-${terme.id}`}
          >
            {enCours ? <SpinnerGap size={13} className="animate-spin" /> : <Sparkle size={13} />}
            {tr("Suggérer (IA)", "Suggest (AI)")}
          </button>
          {!edition && (
            <button
              type="button"
              onClick={() => setEdition(true)}
              className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
              data-testid={`orthographe-editer-${terme.id}`}
              title={tr("Renommer le terme cible", "Rename the target term")}
            >
              <PencilSimple size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => onSupprimer(terme.id, terme.terme)}
            className="p-1.5 rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
            data-testid={`orthographe-supprimer-${terme.id}`}
            title={tr("Supprimer ce terme", "Delete this term")}
          >
            <Trash size={14} />
          </button>
        </div>
      </div>

      {enAttente.length > 0 && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3"
             data-testid={`orthographe-attente-${terme.id}`}>
          <p className="text-[11px] font-semibold text-amber-800 mb-2">
            {tr("Proposé par l'IA — en attente de votre validation. Rien n'est appliqué avant.",
                "Suggested by the AI — awaiting your review. Nothing is applied before that.")}
          </p>
          <div className="flex flex-wrap gap-2">
            {enAttente.map((v) => (
              <span key={v.texte}
                    className="inline-flex items-center gap-1 bg-white border border-amber-300 rounded-full pl-2.5 pr-1 py-0.5 text-xs text-zinc-800">
                {v.texte}
                <button
                  type="button"
                  onClick={() => onStatut(terme.id, v.texte, "active")}
                  className="p-1 rounded-full text-emerald-600 hover:bg-emerald-50"
                  data-testid={`orthographe-valider-${terme.id}-${v.texte}`}
                  title={tr("C'est bien une faute", "This is indeed a mistake")}
                >
                  <Check size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => onStatut(terme.id, v.texte, "refusee")}
                  className="p-1 rounded-full text-rose-600 hover:bg-rose-50"
                  data-testid={`orthographe-refuser-${terme.id}-${v.texte}`}
                  title={tr("Ce n'est pas une faute", "This is not a mistake")}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-3" data-testid={`orthographe-actives-${terme.id}`}>
        {actives.length === 0 && (
          <p className="text-xs text-zinc-400">
            {tr("Aucune variante erronée enregistrée pour ce terme.",
                "No incorrect spelling recorded for this term yet.")}
          </p>
        )}
        {actives.map((v) => (
          <span key={v.texte}
                className="inline-flex items-center gap-1 bg-zinc-50 border border-zinc-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs text-zinc-800">
            {v.texte}
            {v.source === "ia" && (
              <Sparkle size={10} className="text-violet-500"
                       title={tr("Proposé par l'IA puis validé", "Suggested by the AI then approved")} />
            )}
            <button
              type="button"
              onClick={() => onRetirer(terme.id, v.texte)}
              className="p-1 rounded-full text-zinc-400 hover:text-rose-600 hover:bg-rose-50"
              data-testid={`orthographe-retirer-${terme.id}-${v.texte}`}
              title={tr("Retirer cette variante", "Remove this variant")}
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>

      {refusees.length > 0 && (
        <p className="text-[11px] text-zinc-400 mb-3" data-testid={`orthographe-refusees-${terme.id}`}>
          {tr("Refusées (jamais recherchées, jamais reproposées) : ",
              "Rejected (never searched, never suggested again): ")}
          {refusees.map((v) => v.texte).join(", ")}
        </p>
      )}

      <div className="flex items-center gap-2">
        <input
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") ajouter(); }}
          placeholder={tr("Variantes erronées, séparées par des virgules",
                          "Incorrect spellings, comma-separated")}
          className="flex-1 min-w-0 px-3 py-1.5 border border-zinc-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
          data-testid={`orthographe-variante-champ-${terme.id}`}
        />
        <button
          type="button"
          onClick={ajouter}
          disabled={enCours || !saisie.trim()}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#002FA7] text-white text-xs font-medium hover:bg-[#001f7a] transition-colors disabled:opacity-40"
          data-testid={`orthographe-variante-ajouter-${terme.id}`}
        >
          <Plus size={13} />
          {tr("Ajouter", "Add")}
        </button>
      </div>
    </div>
  );
};

export default TermeCard;
